import type { ManagedApiCachingStrategy } from './types';

const SlowFetchTimeoutThreshold = 1000; // something to reconsider/ config driven. Could change based on Pxx number or average.

export type CachingStrategyRead<T> = () => Promise<T | null>;
export type CachingStrategyWrite<T> = (response: T) => Promise<void>;
export type CachingStrategyFetch<T> = () => Promise<T>;
export type CachingStrategy = <T>(
  read: CachingStrategyRead<T>,
  write: CachingStrategyWrite<T>,
  fetch: CachingStrategyFetch<T>
) => Promise<T>;

const fetchAndWriteCacheStrategy: CachingStrategy = async <T>(
  _read: CachingStrategyRead<T>,
  write: CachingStrategyWrite<T>,
  fetch: CachingStrategyFetch<T>
) => {
  const response = await fetch();
  await write(response);

  return response;
};

const fetchOnlyStrategy: CachingStrategy = async <T>(
  _read: CachingStrategyRead<T>,
  _write: CachingStrategyWrite<T>,
  fetch: CachingStrategyFetch<T>
) => fetch();

const readCacheOrFetchStrategy: CachingStrategy = async <T>(
  read: CachingStrategyRead<T>,
  _write: CachingStrategyWrite<T>,
  fetch: CachingStrategyFetch<T>
) => {
  const cachedResponse = await read();

  return cachedResponse ?? fetch();
};

const readCacheAndRevalidate: CachingStrategy = async <T>(
  read: CachingStrategyRead<T>,
  write: CachingStrategyWrite<T>,
  fetch: CachingStrategyFetch<T>
) => {
  const cachedResponse = await read();
  if (!cachedResponse) {
    return fetchAndWriteCacheStrategy(read, write, fetch);
  }

  const callback = async () => {
    const freshResponse = await fetch();
    await write(freshResponse);
  };
  requestIdleCallback(callback);

  return cachedResponse;
};

const readCacheOrFetchWithTimeoutStrategy: CachingStrategy = async <T>(
  read: CachingStrategyRead<T>,
  write: CachingStrategyWrite<T>,
  fetch: CachingStrategyFetch<T>
) => {
  const fetchPromise = fetch().then(async (response) => {
    await write(response);

    return response;
  });

  const timeoutPromise = new Promise<null>((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, SlowFetchTimeoutThreshold);
  });

  const result = await Promise.race([fetchPromise, timeoutPromise]);

  if (result != null) {
    return result;
  }

  const cachedResponse = await read();

  if (cachedResponse) {
    return cachedResponse;
  }

  return fetchPromise;
};

const readCacheOrFetchAndWrite: CachingStrategy = async <T>(
  read: CachingStrategyRead<T>,
  write: CachingStrategyWrite<T>,
  fetch: CachingStrategyFetch<T>
) => {
  const cachedResponse = await read();
  if (!cachedResponse) {
    return fetchAndWriteCacheStrategy(read, write, fetch);
  }

  return cachedResponse;
};

const strategies: Record<ManagedApiCachingStrategy, CachingStrategy> = {
  FetchAndWriteCache: fetchAndWriteCacheStrategy,
  FetchOnly: fetchOnlyStrategy,
  ReadCacheOrFetch: readCacheOrFetchStrategy,
  ReadCacheOrFetchWithTimeout: readCacheOrFetchWithTimeoutStrategy,
  ReadCacheOrFetchWithTimeoutDeferredFulfill: readCacheOrFetchWithTimeoutStrategy,
  ReadCacheOrFetchAndWrite: readCacheOrFetchAndWrite,
  ReadCacheAndRevalidate: readCacheAndRevalidate,
  ReadCacheAndRevalidateWithFulfill: readCacheAndRevalidate,
  ReadCacheAndRevalidateWithDeferredFulfill: readCacheAndRevalidate,
};

type GetCachingStrategy = (strategy?: ManagedApiCachingStrategy) => CachingStrategy;
export const getCachingStrategy: GetCachingStrategy = (strategy?: ManagedApiCachingStrategy) =>
  strategy ? strategies[strategy] : fetchOnlyStrategy;
